/**
 * The one place the prescription notation is built.
 *
 * RPE is spelled out on purpose. A bare "@7" reads as "70%" to anyone who
 * isn't already fluent in the scale, and it can't be rewritten as a percentage
 * anyway: RPE 7 is roughly 80% of 1RM at 5 reps but roughly 72% at 8, so no
 * single number covers a rep range. The range only works because the effort is
 * what's fixed, not the load.
 *
 * What the middle term is — reps, seconds, or metres — follows the target the
 * plan actually carries. An item with no rep band is not a broken rep band; it
 * is a plank.
 */

import type { PlanItem } from '../db'
import { formatDistance, formatDuration, type DistanceUnit } from './units'

/**
 * "3 × 8 @ RPE 7", "3 × 45s", "1 × 2 km in 8:00".
 *
 * It used to take a weight `unit` too. It never rendered a weight — a
 * prescription is sets and reps, and the load is the lifter's business — and
 * once distance stopped being derived from the weight preference the parameter
 * had nothing left to do at all.
 */
export function formatTarget(
  item: PlanItem,
  du: DistanceUnit = 'km',
): string {
  const rpe = item.target_rpe ? ` @ RPE ${item.target_rpe}` : ''

  const measure = (() => {
    if (item.target_distance_m) {
      // A row prescribed as distance and time is both, in that order.
      const distance = formatDistance(item.target_distance_m, du)
      return item.target_duration_seconds
        ? `${distance} in ${formatDuration(item.target_duration_seconds)}`
        : distance
    }
    if (item.target_duration_seconds) return formatDuration(item.target_duration_seconds)
    if (item.target_reps_low || item.target_reps_high) {
      // Prescriptions are a single number now. The range only survives in
      // plans written before that, and those are shown as they were written
      // rather than silently rounded to one end of a band the user chose.
      return item.target_reps_high !== item.target_reps_low
        ? `${item.target_reps_low}-${item.target_reps_high}`
        : `${item.target_reps_low}`
    }
    return null
  })()

  // No measure at all means a malformed item. Say the set count rather than
  // rendering "3 × undefined".
  if (measure === null) return `${item.target_sets} sets${rpe}`

  return `${item.target_sets} × ${measure}${rpe}`
}
